import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@rbx/intl';
import { Grid, Drawer, useMediaQuery, IconButton, CloseIcon } from '@rbx/ui';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import { ReferenceType } from '@modules/client';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import {
  rightNavID,
  rightNavWidth,
  rightNavMinBreakpoint,
} from '@modules/layout/layout/constants/layoutConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useElementsShowInView from '@modules/ui/shared/hooks/useElementsShowInView';
import { EngineReferenceStudioIcon } from '@modules/ui/icons/EngineReferenceStudioIcon';
import { OpenSourceEditButton } from '@modules/openSource/components/OpenSourceEditButton';
import FeedbackButton from '@modules/feedback/components/FeedbackButton';
import LanguageMenu from '@modules/localization/components/LanguageMenu';
import { useRobloxAuthentication } from '@rbx/auth';
import { useShareFeedbackProvider } from '@modules/feedback/context/ShareFeedbackProvider';
import groupsClient from '@modules/clients/groups';
import { getMemberSlug, MEMBER_TYPES_WITH_PARENTHESIS } from '../../utils/apiReferenceUtils';
import { summaryID } from '../../constants/apiReferenceConstants';
import getReferenceMemberIcon from './getReferenceMemberIcon';
import useEngineReferenceRightNavContentStyles from './EngineReferenceRightNavContent.styles';
import { getMemberTypeFromHeading as getMemberTypeFromHeadingUtil } from '../utils/engineReferenceRightNavUtils';

interface EngineReferenceRightNavContentProps {
  headings: HeadingItem[];
  referenceName?: string; // Name of the reference (class, datatype, etc.)
  referenceType?: ReferenceType; // Type of the reference
  open?: boolean; // Controls drawer open state (for mobile)
  onClose?: () => void; // Callback when drawer closes (for mobile)
}

const EngineReferenceRightNavContent: FunctionComponent<
  React.PropsWithChildren<EngineReferenceRightNavContentProps>
> = ({ headings, referenceName, referenceType, open = false, onClose }) => {
  const { translate } = useTranslation();
  const {
    showSecondaryNavigation,
    enableNewLayout,
    contentDirFilePath,
    isOpenSourced,
    machineTranslated,
  } = useAppConfig();
  const { isFetched: isAuthLoaded } = useRobloxAuthentication();
  const { isReady: isAllowFeedbackCheckReady, isDocsFeedbackAllowed } = useShareFeedbackProvider();
  const [isEmployee, setIsEmployee] = useState(false);

  const isDrawerCompatible = useMediaQuery((theme) =>
    theme.breakpoints.down(rightNavMinBreakpoint),
  );

  useEffect(() => {
    groupsClient.isEmployee().then(setIsEmployee);
  }, []);

  const showOpenSourceEditButton =
    isOpenSourced || isEmployee || process.env.environment !== 'production';

  const {
    classes: {
      container,
      listItem,
      listItemIndented,
      listItemActive,
      listItemSection,
      listItemDeprecated,
      listLink,
      listLinkContent,
      listIcon,
      listContainer,
      navReferenceName,
      referenceNameWithClose,
      drawerListContainer,
      drawerCloseButton,
      drawerContent,
      referenceActionButtons,
    },
    cx,
  } = useEngineReferenceRightNavContentStyles({
    showSecondaryNavigation,
    enableNewLayout: enableNewLayout ?? false,
  });
  const { pause, enable, currentElementId, observer } = useElementsShowInView();

  const pauseHandleScroll = useCallback(() => pause(200), [pause]);

  // Enable scroll detection when right nav mounts
  // Note: We intentionally do NOT disable on unmount because:
  // 1. Scroll detection is shared state used by multiple components (breadcrumb, right nav)
  // 2. The scroll handler is already properly cleaned up in ElementsShowInViewProvider
  // 3. Disabling on unmount would break scroll detection for other components
  useEffect(() => {
    enable();
    // only trigger on mount. note that setting enable and disable as dependencies will cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mobile, always render drawer (even if empty) so it can be toggled
  // On desktop, return null if empty (AppRightNav handles empty state)
  if (!isDrawerCompatible && headings.length === 0) {
    return null;
  }

  // Render navigation items (shared between desktop and mobile)
  const renderReferenceNavItems = () => {
    return headings.map((heading, index) => {
      const slug = getMemberSlug(heading.slug);
      const isSelected = currentElementId === slug;
      const memberType = getMemberTypeFromHeadingUtil(heading, index, headings);
      const memberIcon = memberType ? getReferenceMemberIcon(memberType) : null;
      const slugLower = heading.slug.toLowerCase();
      // Indent sub-sections under Summary (summary-properties, summary-methods, summary-events)
      const isSummarySubSection = slugLower.startsWith('summary-') && slugLower !== summaryID;
      // Section headings are items without member icons (Properties, Methods, Events, etc.)
      const isSectionHeading = !memberIcon;
      // Add () suffix for callable members
      const showParenthesis = memberType && MEMBER_TYPES_WITH_PARENTHESIS.has(memberType);
      const displayTitle = showParenthesis ? `${heading.title}()` : heading.title;

      return (
        <div
          key={heading.slug}
          className={cx(listItem, {
            [listItemIndented]: isSummarySubSection,
            [listItemActive]: isSelected,
            [listItemSection]: isSectionHeading,
            [listItemDeprecated]: heading.isDeprecated,
          })}>
          <DynamicallyRouted.Link
            href={`#${slug}`}
            underline='none'
            color='inherit'
            className={listLink}
            onClick={() => {
              pauseHandleScroll();
              // After scroll completes, trigger a scroll event to update active state
              // This ensures the active element updates when clicking a link
              setTimeout(() => {
                // Trigger a scroll event on the observer to update currentElementId
                if (observer) {
                  observer.dispatchEvent(new Event('scroll', { bubbles: true }));
                } else {
                  // Fallback: trigger on window if no observer
                  window.dispatchEvent(new Event('scroll', { bubbles: true }));
                }
              }, 250); // Wait for scroll animation to complete
              // Close drawer on mobile when clicking a link
              if (isDrawerCompatible && onClose) {
                onClose();
              }
            }}>
            <span className={listLinkContent}>
              {memberIcon && <span className={listIcon}>{memberIcon}</span>}
              {displayTitle}
            </span>
          </DynamicallyRouted.Link>
        </div>
      );
    });
  };

  // Render reference name/icon with optional close button (shared between desktop and mobile)
  const renderReferenceName = (showCloseButton: boolean) => {
    if (!referenceName || !referenceType) return null;
    return (
      <div className={cx(listItem, listLink, showCloseButton && referenceNameWithClose)}>
        <span className={listLinkContent}>
          <EngineReferenceStudioIcon
            name={referenceName}
            referenceType={referenceType}
            showFallbackIcon={false}
          />
          <span className={navReferenceName}>{referenceName}</span>
        </span>
        {showCloseButton && onClose && (
          <IconButton
            onClick={onClose}
            aria-label={translate('Label.Close') || 'Close'}
            size='small'
            className={drawerCloseButton}>
            <CloseIcon color='disabled' />
          </IconButton>
        )}
      </div>
    );
  };

  const renderReferenceActionButtons = () => {
    return (
      <div className={referenceActionButtons}>
        <LanguageMenu engineRevampEnabled />
        <FeedbackButton
          pageTitle={referenceName || ''}
          show={!isAuthLoaded || !isAllowFeedbackCheckReady || isDocsFeedbackAllowed}
          engineRevampEnabled
        />
        {showOpenSourceEditButton && contentDirFilePath && (
          <OpenSourceEditButton
            contentDirFilePath={contentDirFilePath}
            isEmployee={isEmployee}
            machineTranslated={machineTranslated}
            engineRevampEnabled
          />
        )}
      </div>
    );
  };

  // Render the content list (shared between desktop and mobile)
  const renderContent = (showCloseButton: boolean) => (
    <React.Fragment>
      {renderReferenceName(showCloseButton)}
      {renderReferenceNavItems()}
      {renderReferenceActionButtons()}
    </React.Fragment>
  );

  // On mobile, render as drawer. On desktop, render normally (handled by AppRightNav)
  if (isDrawerCompatible) {
    return (
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        variant='temporary'
        PaperProps={{
          className: drawerContent,
          style: {
            width: rightNavWidth,
            maxWidth: '90vw',
          },
        }}>
        <Grid container direction='column' height='100%'>
          <Grid item flexGrow={1} className={cx(drawerListContainer)}>
            {renderContent(true)}
          </Grid>
        </Grid>
      </Drawer>
    );
  }

  // Desktop navigation
  return (
    <Grid id={rightNavID} item className={container}>
      <Grid container minWidth={rightNavWidth} maxWidth={rightNavWidth} direction='column'>
        <Grid item flexGrow={1} className={listContainer}>
          {renderContent(false)}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EngineReferenceRightNavContent;
